import { useEffect, useState, useRef } from "react";
import {
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
} from "@mui/material";
import { useParams } from "react-router-dom";
import QRCode from "qrcode.react";
import ReactToPrint from "react-to-print";
import { FetchedData } from "./ContractorFormPage";
import { getJwtTokenFromIdToken } from "../auth/AuthFanc";
import BackButton from "../../atoms/Button/BrowserBackButton";
import image1 from "../../../assets/images/haudiLogo.png";
import image2 from "../../../assets/images/fulltechLogo.png";

interface QrKeyData {
  vendor: string;
  version: string;
  id: string;
  password: string;
  eep?: string;
}

interface QrKeyDataWithId extends QrKeyData {
  autoKeyId: number;
  keyIndexInContract: number;
  doorPlace: string;
}

const MasterKeyQrPage = () => {
  const { autoBuildingId, autoContractId, autoKeyId } = useParams<{
    autoBuildingId: string;
    autoContractId: string;
    autoKeyId?: string;
  }>();
  const qrCodesRef = useRef<HTMLDivElement>(null);

  const [contractData, setContractData] = useState<FetchedData | undefined>(undefined);
  console.log("URLパラメータautoKeyId：" + autoKeyId);

  const [keys, setKeys] = useState<QrKeyDataWithId[]>([]);

  const currentDateTime = new Date().toLocaleString("ja-JP", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  useEffect(() => {
    // 内部の非同期関数を定義
    const fetchData = async () => {
      // JWTトークンを取得します
      const jwtToken = await getJwtTokenFromIdToken();

      try {
        // call showContract API
        const response = await fetch(
          process.env.REACT_APP_API_ENDPOINT +
          `/buildings/${autoBuildingId}/contracts/${autoContractId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: jwtToken,
            },
          },
        );

        const data: FetchedData = await response.json();
        console.log("QR用データ" + JSON.stringify(data));

        setContractData(data);

        const allKeys = data.keys.map(
          (key): QrKeyDataWithId => ({
            autoKeyId: key.autoKeyId,
            keyIndexInContract: key.keyIndexInContract,
            vendor: key.sdVender,
            version: key.sdVersion,
            id: key.activateId,
            password: key.password,
            eep: data.eep,
            doorPlace: key.sdc.doorPlace,
          }),
        );

        let filteredKeys: QrKeyDataWithId[];

        if (autoKeyId === "all") {
          // QR全表示
          filteredKeys = allKeys;
        } else {
          // QR単体表示
          const keyIdNumber = Number(autoKeyId);
          filteredKeys = allKeys.filter((key) => key.autoKeyId === keyIdNumber);
        }

        setKeys(filteredKeys);
      } catch (error) {
        console.error(error);
      }
    };

    // 非同期関数を実行
    fetchData();
  }, [autoBuildingId, autoContractId, autoKeyId]);

  return (
    <>
      <style>
        {`
                @media print {
                    body {
                        width: 210mm;
                        height: 297mm;
                    }
                }
                `}
      </style>
      <Box
        px={5}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography color="error">印刷時はA4縦を指定してください</Typography>
        <BackButton />
      </Box>
      <ReactToPrint
        trigger={() => (
          <Button sx={{ marginLeft: 5 }} variant="contained" color="primary">
            契約情報・QRコードPDF保存/印刷
          </Button>
        )}
        content={() => qrCodesRef.current}
        documentTitle={
          autoKeyId === "all"
            ? `鍵シート_${contractData?.building.buildingId}-${contractData?.contractId}.pdf`
            : `鍵シート_${contractData?.building.buildingId}-${contractData?.contractId}-${contractData?.keys[0]?.sdc.sdcSerialNumber}-${keys[0]?.keyIndexInContract}.pdf`
        }
      />
      <Box ref={qrCodesRef} height="100%">
        {keys.map((key, index) => (
          <Box
            p={"8mm"}
            key={index}
            sx={{ pageBreakAfter: "always" }}
            display={"flex"}
            flexDirection={"column"}
            height="100%"
          >
            <Typography variant="h5" gutterBottom>
              鍵シート（デジタルキー取得用認証情報通知書）
            </Typography>
            <Typography marginTop={1} variant="body1">
              発行日：{currentDateTime}
            </Typography>
            <Typography variant="body1" gutterBottom mt={2}>
              <Typography variant="body1" color="error">
                この「鍵シート」には大切な情報が含まれています。紛失されることのないよう契約書同様に大切に保管してください。
                万が一紛失、またはQRコードが第三者に漏れた場合は鍵の悪用を防ぐため速やかに管理会社までお知らせください。
              </Typography>
              スマートフォンアプリ「FTスマートキー」（デジタルキーホルダー）を利用して、QRコードを読み取ることでデジタルキーを取得できます。使用者欄には、各シートのIDでデジタルキーを使用する人が特定できるように記入しておいてください。
              <br />
              スマートフォンを失くした場合は、このシートを利用して新しいスマートフォンでデジタルキーを再取得できます。その際、前のデジタルキーは無効化されますのでセキュリティが確保されます。
              機種変更の場合も、新しいスマートフォンでこのシートを使ってデジタルキーを取得し、データ移行を行ってください。
              <br />
            </Typography>

            {contractData && (
              <>
                <Box mt={2}>
                  <Typography variant="h6" gutterBottom>
                    ＜契約情報＞
                  </Typography>
                  {/* <Typography variant="body2" gutterBottom>
                    契約管理ID：{contractData.contractManagementId}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    取付先コード（物件管理ID）：{contractData.building.buildingManagementId}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    取付先名（物件名称）：{contractData.building.buildingName}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    物件住所：{contractData.building.buildingAddress}
                  </Typography> */}
                  {/* <Typography variant="body2" gutterBottom>
                    契約者氏名：{contractData.contractorName}
                  </Typography> */}
                  <Typography variant="body1" gutterBottom>
                    契約システムID：{contractData.contractId}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    物件システムID：{contractData.building.buildingId}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    管理区分：{contractData.roomNumber}
                  </Typography>
                </Box>
              </>
            )}
            <Card sx={{ width: 420, mt: 2, p: 1 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  鍵No：{key.keyIndexInContract}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  ID：{key.id}
                </Typography>
                {/* <Typography variant="body1" gutterBottom>
                  パスワード：{key.password}
                </Typography> */}
                <Typography variant="body1" gutterBottom>
                  対象ドア：{key.doorPlace}
                </Typography>

                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  my={3}
                >
                  {(() => {
                    // QRCodeのvalue用のオブジェクトを動的に生成
                    const baseQrValueObj = {
                      vendor: key.vendor,
                      version: key.version,
                      id: key.id,
                      password: key.password,
                    };

                    // eepが空文字やundefinedでなければ、baseQrValueObjとマージ
                    const qrValueObj =
                      key.eep && key.eep.trim() !== ""
                        ? { ...baseQrValueObj, eep: key.eep }
                        : baseQrValueObj;
                    return (
                      <QRCode
                        value={JSON.stringify(qrValueObj)}
                        size={160}
                        renderAs="svg"
                      />
                    );
                  })()}
                </Box>

                <Typography variant="body1" gutterBottom>
                  鍵使用者： _________________________
                </Typography>
                <Typography variant="body1" gutterBottom>
                  （使用者利用欄）
                </Typography>
              </CardContent>
            </Card>
            <Box
              display="flex"
              justifyContent="flex-end"
              sx={{ marginTop: "auto" }}
            >
              <img
                src={image2}
                alt="fulltechLogo"
                style={{
                  height: "30px",
                  alignSelf: "center",
                }}
              />
              <img
                src={image1}
                alt="haudiLogo"
                style={{
                  height: "30px",
                  marginLeft: "21px",
                  alignSelf: "center",
                }}
              />
            </Box>
          </Box>
        ))}
      </Box>
    </>
  );
};

export default MasterKeyQrPage;
